import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaStethoscope,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaPaperPlane,
} from "react-icons/fa";
import { buildApiUrl, API_ENDPOINTS } from "../../utils/apiConfig";
import { CLINIC_GOOGLE_MAPS_LINK } from "../../utils/addressUtils";
import { getTimeSlotsForDate } from "../../utils/appointmentSlots";
import {
  HELPDESK_PHONE_DISPLAY,
  EMERGENCY_PHONE_DISPLAY,
  HELPDESK_PHONE_TEL,
  EMERGENCY_PHONE_TEL,
} from "../../utils/clinicContact";
import "./Contact.css";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    age: "",
    sex: "",
    serviceRequired: "",
    preferredDate: "",
    preferredTime: "",
    additionalInfo: "",
    patientAddress: "",
    patientOccupation: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Input changed: ${name} = ${value}`);

    setFormData((prev) => {
      const newData = { ...prev, [name]: value };

      if (name === "preferredDate") {
        newData.preferredTime = "";
      }

      console.log("Updated form data:", newData);
      return newData;
    });

    if (name === "preferredDate" && value) {
      fetchAvailableSlots(value);
    }
  };

  // Fetch available time slots for a specific date
  const fetchAvailableSlots = async (date) => {
    if (!date) return;

    setIsLoadingSlots(true);
    try {
      const response = await fetch(
        buildApiUrl(`${API_ENDPOINTS.APPOINTMENTS.SLOTS}?date=${date}`)
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setBookedSlots(data.bookedSlots);
        }
      }
    } catch (error) {
      console.error("Error fetching available slots:", error);
    } finally {
      setIsLoadingSlots(false);
    }
  };

  // Check if a time slot is booked
  const isSlotBooked = (timeValue) => {
    return bookedSlots.some((slot) => slot.value === timeValue);
  };

  // Check if a time slot is in the past for today
  const isTimeSlotInPast = (dateString, timeValue) => {
    if (!dateString || !timeValue) return false;

    const today = new Date().toISOString().split("T")[0];
    if (dateString !== today) return false;

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    const [slotHour, slotMinute] = timeValue.split(":").map(Number);

    // Convert to minutes for easier comparison
    const currentTimeInMinutes = currentHour * 60 + currentMinute;
    const slotTimeInMinutes = slotHour * 60 + slotMinute;

    return slotTimeInMinutes <= currentTimeInMinutes;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Debug: Log form data to see what's being submitted
    console.log("Form data being submitted:", formData);

    // Validate required fields
    if (
      !formData.name.trim() ||
      !formData.phone.trim() ||
      !formData.age ||
      !formData.sex.trim() ||
      !formData.serviceRequired ||
      !formData.preferredDate ||
      !formData.preferredTime
    ) {
      console.log("Validation failed. Missing fields:");
      console.log("name:", formData.name, "valid:", !!formData.name.trim());
      console.log("phone:", formData.phone, "valid:", !!formData.phone.trim());
      console.log("age:", formData.age, "valid:", !!formData.age);
      console.log("sex:", formData.sex, "valid:", !!formData.sex.trim());
      console.log(
        "serviceRequired:",
        formData.serviceRequired,
        "valid:",
        !!formData.serviceRequired
      );
      console.log(
        "preferredDate:",
        formData.preferredDate,
        "valid:",
        !!formData.preferredDate
      );
      console.log(
        "preferredTime:",
        formData.preferredTime,
        "valid:",
        !!formData.preferredTime
      );
      setSubmitStatus("error");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.APPOINTMENTS.BOOK),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            patientName: formData.name,
            patientEmail: formData.email,
            patientPhone: formData.phone,
            patientAge: formData.age,
            patientSex: formData.sex,
            serviceRequired: formData.serviceRequired,
            preferredDate: formData.preferredDate,
            preferredTime: formData.preferredTime,
            additionalInfo: formData.additionalInfo,
            patientAddress: formData.patientAddress,
            patientOccupation: formData.patientOccupation,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit appointment");
      }

      const result = await response.json();
      if (result.success) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          email: "",
          phone: "",
          age: "",
          sex: "",
          serviceRequired: "",
          preferredDate: "",
          preferredTime: "",
          additionalInfo: "",
          patientAddress: "",
          patientOccupation: "",
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    "Angiography",
    "Angioplasty",
    "Stent Placement",
    "Valvuloplasty",
    "TAVR",
    "Mitral Valve Repair/Replacement",
    "Fractional Flow Reserve (FFR)",
    "Chronic Total Occlusion (CTO)",
    "Atherectomy",
    "Pericardiocentesis",
    "Cardiac Catheterization",
    "Endovenous Thermal Ablation",
    "ECG",
    "Heart Checkup",
    "Stress Test",
    "Echocardiography",
    "Preventive Cardiology",
    "Cardiac Consultation",
    "Percutaneous Device Closures",
    "Pacemaker Implantation",
    "Cardiac Resynchronising Therapy (CRT)",
    "AICD Implantation",
    "Health CAMP",
  ];

  const timeSlots = getTimeSlotsForDate(formData.preferredDate);

  // Get minimum date (today) in IST
  const getMinDate = () => {
    const now = new Date();
    // Convert to IST (UTC+5:30)
    const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds
    const istTime = new Date(now.getTime() + istOffset);
    return istTime.toISOString().split("T")[0];
  };

  // Check if selected date is in the past (IST)
  const isDateInPast = (dateString) => {
    if (!dateString) return false;
    const selectedDate = new Date(dateString);
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istNow = new Date(now.getTime() + istOffset);
    return selectedDate < istNow;
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <motion.div
            className="contact-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 style={{ fontSize: "1.8rem" }}>
              Contact Us & Book Appointment
            </h1>
            <p style={{ fontSize: "var(--font-size-base)" }}>
              Ready to take control of your heart health? Get in touch with us
              today.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container">
        <div className="contact-content">
          {/* Contact Information */}
          <motion.div
            className="contact-info"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2>Get In Touch</h2>
            <p>
              We're here to help with all your cardiology needs. Contact us or
              book an appointment below.
            </p>

            <div className="contact-details">
              <div className="page-contact-item">
                <FaMapMarkerAlt className="page-contact-icon" />
                <div>
                  <h4>Address</h4>
                  <p>
                    <a
                      href={CLINIC_GOOGLE_MAPS_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="address-link"
                      title="Click to open in Google Maps"
                    >
                      Shop No-6, Shopping Complex, Vasanth Nagar,
                      <br />
                      IDPL Staff Cooperative Housing Society,
                      <br />
                      Kukatpally, Hyderabad, Telangana 500085
                    </a>
                  </p>
                </div>
              </div>

              <div className="page-contact-item page-contact-item--phone">
                <FaPhone className="page-contact-icon" />
                <div className="page-contact-item-content">
                  <h4>Phone</h4>
                  <p>
                    <a href={`tel:${HELPDESK_PHONE_TEL}`}>
                      Helpdesk: {HELPDESK_PHONE_DISPLAY}
                    </a>
                    <br />
                    <a href={`tel:${EMERGENCY_PHONE_TEL}`}>
                      Emergency: {EMERGENCY_PHONE_DISPLAY}
                    </a>
                  </p>
                </div>
              </div>

              <div className="page-contact-item page-contact-item--email">
                <FaEnvelope className="page-contact-icon" />
                <div className="page-contact-item-content">
                  <h4>Email</h4>
                  <p>
                    <a href="mailto:satya.s.nimmagadda@gmail.com">
                      satya.s.nimmagadda@gmail.com
                    </a>
                  </p>
                </div>
              </div>

              <div className="page-contact-item">
                <FaClock className="page-contact-icon" />
                <div>
                  <h4>Hours</h4>
                  <p>Monday to Saturday, 6:00 PM - 9:00 PM</p>
                  <small className="contact-hours-note">
                    A prior appointment is preferred.
                  </small>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Appointment Form */}
          <motion.div
            className="appointment-form"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2>Book Your Appointment</h2>
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="fullName">
                  <FaUser style={{ color: "#e03e33" }} />
                  Full Name <span className="required-asterisk">*</span>
                </label>
                <div className="input-with-icon">
                  <input
                    type="text"
                    id="fullName"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  <FaEnvelope style={{ color: "#e03e33" }} />
                  Email
                </label>
                <div className="input-with-icon">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phoneNumber">
                  <FaPhone style={{ color: "#e03e33" }} />
                  Phone Number <span className="required-asterisk">*</span>
                </label>
                <div className="input-with-icon">
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="patientAge">
                    <FaUser style={{ color: "#e03e33" }} />
                    Age <span className="required-asterisk">*</span>
                  </label>
                  <div className="input-with-icon">
                    <input
                      type="number"
                      id="patientAge"
                      name="age"
                      value={formData.age}
                      onChange={handleInputChange}
                      placeholder="Enter your age"
                      min="1"
                      max="120"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="patientSex">
                    <FaUser style={{ color: "#e03e33" }} />
                    Sex <span className="required-asterisk">*</span>
                  </label>
                  <div className="input-with-icon">
                    <select
                      id="patientSex"
                      name="sex"
                      value={formData.sex}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select sex</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="patientAddress">
                  <FaMapMarkerAlt style={{ color: "#e03e33" }} />
                  Patient Address
                </label>
                <div className="input-with-icon">
                  <textarea
                    id="patientAddress"
                    name="patientAddress"
                    value={formData.patientAddress}
                    onChange={handleInputChange}
                    placeholder="Enter your complete address"
                    rows="3"
                  ></textarea>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="patientOccupation">
                  <FaUser style={{ color: "#e03e33" }} />
                  Patient Occupation
                </label>
                <div className="input-with-icon">
                  <input
                    type="text"
                    id="patientOccupation"
                    name="patientOccupation"
                    value={formData.patientOccupation}
                    onChange={handleInputChange}
                    placeholder="Enter your occupation/profession"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="serviceRequired">
                  <FaStethoscope style={{ color: "#e03e33" }} />
                  Service Required <span className="required-asterisk">*</span>
                </label>
                <div className="input-with-icon">
                  <select
                    id="serviceRequired"
                    name="serviceRequired"
                    value={formData.serviceRequired}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a service</option>
                    {services.map((service, index) => (
                      <option key={index} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="preferredDate">
                    <FaCalendarAlt style={{ color: "#e03e33" }} />
                    Preferred Date <span className="required-asterisk">*</span>
                  </label>
                  <div className="input-with-icon">
                    <input
                      type="date"
                      id="preferredDate"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleInputChange}
                      required
                      min={getMinDate()}
                      disabled={isDateInPast(formData.preferredDate)}
                    />
                  </div>
                  <small className="appointment-hours-note">
                    Monday to Saturday, 6:00 PM - 9:00 PM
                    <br />
                    A prior appointment is preferred.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="preferredTime">
                    <FaClock style={{ color: "#e03e33" }} />
                    Preferred Time <span className="required-asterisk">*</span>
                  </label>
                  <div className="input-with-icon">
                    <select
                      id="preferredTime"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleInputChange}
                      required
                      disabled={isLoadingSlots}
                    >
                      <option value="">
                        {isLoadingSlots
                          ? "Loading time slots..."
                          : "Select time"}
                      </option>
                      {timeSlots.map((time, index) => {
                        const isBooked = isSlotBooked(time.value);
                        const isPast = isTimeSlotInPast(
                          formData.preferredDate,
                          time.value
                        );
                        const isDisabled = isBooked || isPast;
                        return (
                          <option
                            key={index}
                            value={time.value}
                            disabled={isDisabled}
                            data-past={isPast}
                            style={{
                              color: isDisabled ? "#999" : "#000",
                              fontStyle: isDisabled ? "italic" : "normal",
                            }}
                          >
                            {time.display}{" "}
                            {isBooked ? "(Booked)" : isPast ? "(Past)" : ""}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  {isLoadingSlots && (
                    <small style={{ color: "#666", fontStyle: "italic" }}>
                      Loading available time slots...
                    </small>
                  )}
                  {!isLoadingSlots && bookedSlots.length > 0 && (
                    <small style={{ color: "#ff8c00" }}>
                      ⚠️ Some time slots are already booked.
                    </small>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="additionalInfo">Additional Information</label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Any specific concerns or information you'd like to share..."
                ></textarea>
              </div>

              {submitStatus && (
                <div className={`submit-status ${submitStatus}`}>
                  {submitStatus === "success" ? (
                    <p>✅ Appointment request sent successfully!</p>
                  ) : (
                    <p>
                      ❌{" "}
                      {submitStatus === "error"
                        ? "Please fill in all required fields and try again."
                        : "Failed to submit. Please try again."}
                    </p>
                  )}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-large"
                disabled={isSubmitting}
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: "20px",
                  padding: "15px 30px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  opacity: isSubmitting ? 0.7 : 1,
                  backgroundColor: "#e03e33",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.backgroundColor = "#c53030";
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.target.style.backgroundColor = "#e03e33";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
                  }
                }}
              >
                {isSubmitting ? (
                  <span>Submitting...</span>
                ) : (
                  <>
                    <FaPaperPlane style={{ marginRight: "8px" }} />
                    Book Appointment
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
